/**
 * Login Form Component
 * Handles user authentication with email and password
 */

import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { loginSchema } from "../../utils/validators";
import {
  resolvePostLoginRoute,
  isSafeRelativePath,
} from "../../utils/roleRouting";
import type { LoginCredentials } from "../../types/auth.types";
import Button from "../common/Button";
import Input from "../common/Input";

const LoginForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState<string>("");
  const [sessionExpiredMessage, setSessionExpiredMessage] =
    useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Check for session expiry message or password reset success in URL params
  useEffect(() => {
    const expired = searchParams.get("expired");
    const reason = searchParams.get("reason");
    const reset = searchParams.get("reset");

    if (expired === "true" && reason) {
      setSessionExpiredMessage(decodeURIComponent(reason));
      // Clear URL params after showing message
      window.history.replaceState({}, "", "/login");

      // Auto-hide message after 10 seconds
      const timeout = setTimeout(() => {
        setSessionExpiredMessage("");
      }, 10000);

      return () => clearTimeout(timeout);
    }

    if (reset === "success") {
      setSuccessMessage(
        "Password reset successful! You can now log in with your new password.",
      );
      // Clear URL params after showing message
      window.history.replaceState({}, "", "/login");

      // Auto-hide message after 10 seconds
      const timeout = setTimeout(() => {
        setSuccessMessage("");
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [searchParams]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginCredentials) => {
    console.log("[LoginForm] Starting login submission");
    try {
      setError("");
      console.log("[LoginForm] Calling login function");
      const response = await login(data);
      console.log("[LoginForm] Login returned:", {
        hasUser: !!response?.user,
        role: response?.user?.role,
      });

      // Read the saved returnTo (stored by RequireAuth / forceLogout when a
      // protected page bounced the user to /login) and always clear it.
      const returnTo = sessionStorage.getItem("returnTo");
      sessionStorage.removeItem("returnTo");

      // Redirect based on user role via the shared, authorization-aware
      // resolver. A saved returnTo is honored ONLY when it is a safe relative
      // path AND this role is actually allowed to view it; otherwise the role
      // default is used. This is what stops a support agent (whose returnTo was
      // "/dashboard" — a page they can't access) from landing on Access Denied.
      // Using window.location.href instead of navigate() to ensure a full page
      // load, clearing any stale state after a session timeout.
      if (response?.user) {
        const userRole = response.user.role;
        const destination = resolvePostLoginRoute(returnTo, userRole);
        console.log(
          "[LoginForm] Resolved post-login destination:",
          destination,
          "(role:",
          userRole,
          ", returnTo:",
          returnTo,
          ")",
        );
        window.location.href = destination;
      } else {
        // Fallback if login doesn't return user data: we have no role to
        // authorize against, so only honor a syntactically safe returnTo.
        const safeReturn =
          returnTo && isSafeRelativePath(returnTo) ? returnTo : "/dashboard";
        console.log(
          "[LoginForm] No user in response, redirecting to",
          safeReturn,
        );
        window.location.href = safeReturn;
      }
    } catch (err: any) {
      console.error("[LoginForm] Login error:", err);
      // (A "2FA_REQUIRED" → /2fa branch was removed 2026-10-03: nothing threw it and /2fa never
      // existed. See the note in services/auth.service.ts.)
      console.log("[LoginForm] Setting error:", err.message);

      const msg = (err.message || "").toString();
      const isNetworkError =
        msg === "Failed to fetch" ||
        msg.includes("NetworkError") ||
        msg.includes("ERR_NAME_NOT_RESOLVED") ||
        msg.includes("ERR_CONNECTION") ||
        msg.includes("ERR_INTERNET_DISCONNECTED") ||
        (err.name === "TypeError" && msg.toLowerCase().includes("fetch"));

      if (isNetworkError) {
        // Surfaces during upstream DNS / network incidents (e.g.,
        // Supabase .co TLD resolver issue 2026-06-08). The fetch
        // never reaches the server, so there's nothing we can do
        // server-side — guide the user to the steps that actually
        // work for a stale DNS cache.
        setError(
          "We can't reach the sign-in service from your network. " +
            "Try, in order: (1) restart your computer; (2) restart " +
            "your WiFi router; (3) switch to your phone's hotspot " +
            "temporarily. If you keep seeing this, contact support.",
        );
      } else if (msg.includes("timeout")) {
        setError(
          "Login timeout - the page may have stale session data. Please refresh the page and try again.",
        );

        // Auto-refresh after 3 seconds
        setTimeout(() => {
          console.log("[LoginForm] Auto-refreshing page after timeout");
          window.location.reload();
        }, 3000);
      } else {
        setError(err.message || "Invalid email or password");
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {sessionExpiredMessage && (
        <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
          <p className="text-sm text-yellow-800 font-medium">
            {sessionExpiredMessage}
          </p>
          <p className="text-xs text-yellow-700 mt-1">
            Please log in again to continue.
          </p>
        </div>
      )}

      {successMessage && (
        <div className="p-3 rounded-lg bg-green-50 border border-green-200">
          <p className="text-sm text-green-800 font-medium">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <Input
        {...register("email")}
        type="email"
        label="Email"
        placeholder="Enter your email"
        error={errors.email?.message}
        fullWidth
        required
      />

      <Input
        {...register("password")}
        type="password"
        label="Password"
        placeholder="Enter your password"
        error={errors.password?.message}
        fullWidth
        required
      />

      <div className="flex items-center justify-between">
        <label className="flex items-center">
          <input
            type="checkbox"
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-600">Remember me</span>
        </label>

        <a
          href="/forgot-password"
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          Forgot password?
        </a>
      </div>

      <Button
        type="submit"
        variant="primary"
        fullWidth
        isLoading={isSubmitting}
      >
        Sign In
      </Button>
    </form>
  );
};

export default LoginForm;
